import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React from "react";
import { MdCheck, MdFileCopy, MdHighlightOff } from "react-icons/md";
import { BsInfoCircleFill } from "react-icons/bs";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow } from "@chakra-ui/react";

const InfoModal = ({ info, varient, type }: { info?: any; varient?: string; type: any }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [copied, setCopied] = React.useState<boolean>(false);
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);

  const isRequest = type === "request";
  const title = isRequest ? "Request Data" : "Response Data";

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 300);
  };

  let parsedData: any = {};
  try {
    parsedData = JSON.parse(isRequest ? info?.requestData : info?.responseData);
  } catch {
    parsedData = isRequest ? info?.requestData : info?.responseData;
  }

  const jsonString = JSON.stringify(parsedData, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      <Popover trigger="hover" placement="bottom">
        <PopoverTrigger>
          <span className="inline-flex">
            <button
              className={`im-trigger-btn${isRequest ? " im-trigger-btn--blue" : " im-trigger-btn--sky"}`}
              onClick={() => onOpen()}
              disabled={!info}
              aria-label={isRequest ? "View request" : "View response"}
            >
              <BsInfoCircleFill size={13} />
              <span>{isRequest ? "Request" : "Response"}</span>
            </button>
          </span>
        </PopoverTrigger>
        <PopoverContent
          border={`1px solid ${isRequest ? "#bfdbfe" : "#bae6fd"}`}
          borderRadius="12px"
          bg={isRequest ? "#eff6ff" : "#f0f9ff"}
          maxW="200px" p="0" overflow="hidden" zIndex="20"
        >
          <PopoverHeader
            bg={isRequest ? "#dbeafe" : "#e0f2fe"}
            fontSize="12px" fontWeight="700"
            color={isRequest ? "#1e40af" : "#0369a1"}
            borderBottom={`1px solid ${isRequest ? "#bfdbfe" : "#bae6fd"}`}
            px="12px" py="9px"
          >
            {isRequest ? "View Request Data" : "View Response Data"}
          </PopoverHeader>
          <PopoverArrow bg={isRequest ? "#eff6ff" : "#f0f9ff"} />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />
        <div className={`im-panel${isOpen && !isCloseModal ? " im-panel--open" : " im-panel--close"}`}>

          {/* Animated top stripe */}
          <div className={`im-top-glow${isRequest ? " im-top-glow--blue" : " im-top-glow--sky"}`} />

          {/* Header */}
          <div className={`im-header${isRequest ? " im-header--blue" : " im-header--sky"}`}>
            <div className="im-header-brand">
              <div className="im-header-logo">
                {isRequest ? (
                  /* Upload / outgoing arrow */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 16 12 12 8 16"/>
                    <line x1="12" y1="12" x2="12" y2="21"/>
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                  </svg>
                ) : (
                  /* Download / incoming arrow */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="8 17 12 21 16 17"/>
                    <line x1="12" y1="12" x2="12" y2="21"/>
                    <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"/>
                  </svg>
                )}
              </div>
              <div>
                <h2 className="im-header-title">{title}</h2>
                <p className="im-header-sub">
                  {isRequest ? info?.endpoint || "API endpoint data" : "Server response payload"}
                </p>
              </div>
            </div>
            <button className="im-close-btn" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          {/* Meta row */}
          <div className="im-meta-row">
            <div className="im-meta-item">
              <span className="im-meta-label">Type</span>
              <span className={`im-meta-badge${isRequest ? " im-meta-badge--blue" : " im-meta-badge--sky"}`}>
                {isRequest ? "Request" : "Response"}
              </span>
            </div>
            <div className="im-meta-item">
              <span className="im-meta-label">Endpoint</span>
              <span className="im-meta-value">{info?.endpoint || "—"}</span>
            </div>
            <div className="im-meta-item">
              <span className="im-meta-label">Client</span>
              <span className="im-meta-value">{info?.clientName || "—"}</span>
            </div>
          </div>

          {/* JSON body */}
          <div className="im-body">
            <div className="im-json-toolbar">
              <span className="im-json-label">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
                JSON Payload
              </span>
              <button className={`im-copy-btn${copied ? " im-copy-btn--done" : ""}`} onClick={handleCopy}>
                {copied ? (
                  <><MdCheck size={13}/> Copied!</>
                ) : (
                  <><MdFileCopy size={13}/> Copy</>
                )}
              </button>
            </div>
            <div className="im-json-wrap">
              <pre className="im-json">{jsonString}</pre>
            </div>
          </div>

          {/* Footer */}
          <div className="im-footer">
            <span className="im-footer-hint">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              Read-only view
            </span>
            <button className="im-btn-close" onClick={handleClose}>Close</button>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* Trigger buttons */
        .im-trigger-btn {
          display:inline-flex; align-items:center; gap:5px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700;
          padding:5px 11px; border-radius:8px; border:1.5px solid; cursor:pointer;
          transition:all .15s; white-space:nowrap;
        }
        .im-trigger-btn:disabled { opacity:.4; cursor:not-allowed; }
        .im-trigger-btn--blue { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }
        .im-trigger-btn--blue:hover:not(:disabled) { background:#dbeafe; border-color:#93c5fd; }
        .im-trigger-btn--sky  { background:#f0f9ff; border-color:#bae6fd; color:#0284c7; }
        .im-trigger-btn--sky:hover:not(:disabled)  { background:#e0f2fe; border-color:#7dd3fc; }

        /* Panel */
        .im-panel {
          font-family:'Plus Jakarta Sans',sans-serif;
          position:fixed; top:0; right:0; bottom:0;
          width:100%; max-width:620px;
          background:#fff; z-index:1004;
          display:flex; flex-direction:column;
          box-shadow:-12px 0 60px rgba(15,23,42,.18);
          transition:transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow:hidden;
        }
        .im-panel--open  { transform:translateX(0); }
        .im-panel--close { transform:translateX(100%); }

        /* Top glow stripe */
        .im-top-glow { height:3px; flex-shrink:0; background-size:200% 100%; animation:imGlow 3s linear infinite; }
        .im-top-glow--blue { background:linear-gradient(90deg,#1e3a8a,#2563eb,#7c3aed,#2563eb,#1e3a8a); }
        .im-top-glow--sky  { background:linear-gradient(90deg,#0369a1,#0284c7,#06b6d4,#0284c7,#0369a1); }
        @keyframes imGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .im-header { padding:15px 24px; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .im-header--blue { background:linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 60%,#2563eb 100%); }
        .im-header--sky  { background:linear-gradient(135deg,#075985 0%,#0284c7 60%,#0ea5e9 100%); }
        .im-header-brand { display:flex; align-items:center; gap:13px; }
        .im-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .im-header-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .im-header-sub   { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; max-width:320px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .im-close-btn { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; }
        .im-close-btn:hover { color:#fff; }

        /* Meta row */
        .im-meta-row { display:flex; align-items:center; gap:0; border-bottom:1px solid #f1f5f9; flex-shrink:0; background:#f8fafc; }
        .im-meta-item { flex:1; padding:11px 16px; display:flex; flex-direction:column; gap:3px; border-right:1px solid #f1f5f9; }
        .im-meta-item:last-child { border-right:none; }
        .im-meta-label { font-size:10.5px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.5px; }
        .im-meta-value { font-size:12.5px; font-weight:600; color:#0f172a; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .im-meta-badge { display:inline-flex; align-items:center; padding:2px 8px; border-radius:6px; font-size:11px; font-weight:700; }
        .im-meta-badge--blue { background:#eff6ff; border:1px solid #bfdbfe; color:#2563eb; }
        .im-meta-badge--sky  { background:#f0f9ff; border:1px solid #bae6fd; color:#0284c7; }

        /* Body */
        .im-body { flex:1; display:flex; flex-direction:column; overflow:hidden; }
        .im-json-toolbar { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; border-bottom:1px solid #f1f5f9; flex-shrink:0; }
        .im-json-label { display:flex; align-items:center; gap:6px; font-size:12px; font-weight:700; color:#374151; }
        .im-copy-btn {
          display:inline-flex; align-items:center; gap:5px;
          font-family:'Plus Jakarta Sans',sans-serif; font-size:12px; font-weight:700;
          padding:6px 12px; border-radius:8px; border:1.5px solid #e2e8f0; background:#fff;
          color:#64748b; cursor:pointer; transition:all .15s;
        }
        .im-copy-btn:hover { background:#f1f5f9; color:#374151; }
        .im-copy-btn--done { border-color:#bbf7d0; background:#f0fdf4; color:#16a34a; }
        .im-json-wrap { flex:1; overflow:auto; padding:0 20px 20px; }
        .im-json-wrap::-webkit-scrollbar { width:4px; height:4px; }
        .im-json-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .im-json {
          font-family:'Fira Code','Cascadia Code','Consolas',monospace;
          font-size:12.5px; line-height:1.7; color:#1e293b;
          background:#f8fafc; border:1px solid #e2e8f0; border-radius:12px;
          padding:16px 18px; margin-top:14px; white-space:pre-wrap; word-break:break-all;
          min-height:100%;
        }

        /* Footer */
        .im-footer { padding:12px 24px; border-top:1px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .im-footer-hint { display:flex; align-items:center; gap:5px; font-size:12px; color:#94a3b8; font-weight:500; }
        .im-btn-close { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:8px 18px; border-radius:9px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .im-btn-close:hover { background:#f1f5f9; color:#374151; }

        @media (max-width:640px) { .im-panel { max-width:100%; } .im-meta-row { flex-wrap:wrap; } .im-meta-item { border-bottom:1px solid #f1f5f9; border-right:none; } }
      `}</style>
    </>
  );
};

export default InfoModal;